import {React, useEffect, useState, useRef, useMemo} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import classes from './Analytics.module.css'
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Dropdown } from "primereact/dropdown";
import { Column } from 'primereact/column';
import { DataTable } from "primereact/datatable";
import { getSiteAnalytics, getSchedules } from "../../../Api Methods/Api";
import { authActions } from "../../../store/auth-Slice";
import DataManager from "../../../hooks/DataManager";
import danger from "../../../assets/portalAssets/danger.png"; 
import dangerOrange from "../../../assets/portalAssets/dangerOrange.png";
import dangerBlue from "../../../assets/portalAssets/dangerBlue.png";
import mixpanel from "mixpanel-browser";
import { Tooltip } from "primereact/tooltip";

const C = {
    green: "#60e08f",
    red: "#ee6d6d",
    orange: "#e8944f",
    blue: "#6298ee",
    lightBlue: "#1eb9c7"
};

const AnalyticsPage = ({ siteUUID, apiData, iglooLocks, isCollapsed, OutPutsPinNames, siteData, hasGateways }) => {

    const [activeTab, setActiveTab] = useState(0);
    const [selectedTime, setSelectedTime] = useState("7D");
    const [scheduleFailsData, setScheduleFailsData] = useState([]);
    const [scheduleSuccesses, setScheduleSuccesses] = useState(null);
    const [securityData, setSecurityData] = useState([]);
    const [unlockData, setUnlockData] = useState([]);
    const [outputData, setOutputData] = useState([]);
    const [topOutputData, setTopOutputData] = useState([]);
    const [topUnlockData, setTopUnlockData] = useState([]);
    const [selectedLock, setSelectedLock] = useState(null);

    const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);
    const [isLoadingSecurity, setIsLoadingSecurity] = useState(true);
    const [isLoadingLocks, setIsLoadingLocks] = useState(true);
    const [isCheckingTabs, setIsCheckingTabs] = useState(true);
    const [hasSchedules, setHasSchedules] = useState(true);

    const scheduleRequestId = useRef(0);
    const securityRequestId = useRef(0);
    const lockRequestId = useRef(0);

    const now = new Date();    
    const last7 = new Date();
    last7.setDate(now.getDate() - 7);
    const last30 = new Date();
    last30.setDate(now.getDate() - 30);
    const last90 = new Date();
    last90.setDate(now.getDate() - 90);

    // cache  
    const cache = useRef({
        schedules: {},
        gateway: {},
        locks: {},
        outputs: {}
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const dataManager = new DataManager();

    const gatewayBattery = siteData.data[0].gatewayBattery?.battery ?? null;   
    const gatewayStatus = siteData.data[0].gatewayBattery?.status || null;

    const isOffline = gatewayStatus === "stale";

    // takes in batterysignal res and parses
    const parseBatterySignal = (batterySignal) => {
        if (!batterySignal) return { battery: null, signal: null };

        const batteryMatch = batterySignal.match(/battery:\s*(\d+)%/i);
        const signalMatch = batterySignal.match(/signal:\s*(\d+)%/i);

        return {
            battery: batteryMatch ? parseInt(batteryMatch[1], 10) : null,
            signal: signalMatch ? parseInt(signalMatch[1], 10) : null
        };
    };

    // get last reading per day with parsed battery/signal and invalid pin count
    const processGatewayData = (rawData) => {
        if (!rawData || rawData.length === 0) return [];

        const dateMap = {};

        rawData.forEach(item => {
            const date = item.event_date;

            // if doesnt exist
            if (!dateMap[date]) {
                dateMap[date] = {
                    event_date: date,
                    battery: null,
                    signal: null,
                    invalid_pin_count: 0
                };
            }

            if (item.subType === 'invalidPin') {
                // invalid pin
                dateMap[date].invalid_pin_count += 1;
            } else if (item.subType === 'battery') {
                // battery/signal
                const parsed = parseBatterySignal(item.batterySignal);
                dateMap[date].battery = parsed.battery;
                dateMap[date].signal = parsed.signal;
            }
        });

        // sort by date
        return Object.values(dateMap).sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    };

    const handleUserClick = (userID, fullName) => {
        dataManager.setItem('clickedUserID', userID);
        localStorage.setItem('clickedUserID', userID);
        localStorage.setItem('ClickedUserFullName', fullName);
        dispatch(authActions.setClickedUserDetails({ userID, fullName }));
        navigate('/Dashboard/Users/UserSites/');
    };

    const dropdownOptions = useMemo(() =>
        OutPutsPinNames?.filter(output => output.active === 1).map(output => {
            // if output has a padlock linked to it
            const linkedLock = iglooLocks?.find(lock => lock && String(lock.outputPin) === String(output.Pin));

            if (linkedLock) {
                return {
                    label: `${output.pinName} - ${linkedLock.lockID}`,
                    value: linkedLock.lockID,
                    type: 'padlock',
                    pin: output.Pin
                };
            } else {
                // regular output with no lock
                return {
                    label: output.pinName,
                    value: `output-${output.Pin}`,
                    type: 'output',
                    pin: output.Pin
                };
            }
        }) || []
    , [OutPutsPinNames, iglooLocks]);

    const tabs = [
        "User Analytics",
        //hasSchedules && "Schedules",
        //hasGateways && "Gateway",     temporary
    ].filter(Boolean)
    const times = ["7D", "1M", "3M"]
    const selectedOption = dropdownOptions.find(option => option.value === selectedLock);

    const getScheduleData = async (selectedTime) => {
        // check cache
        if (cache.current.schedules[selectedTime]) {
            const cached = cache.current.schedules[selectedTime];
            setScheduleFailsData(cached.failures);
            setScheduleSuccesses(cached.totalSuccess?.total_success || 0);
            setIsLoadingSchedules(false);
            return;
        }

        const currentRequestId = ++scheduleRequestId.current;
        setIsLoadingSchedules(true);
        setScheduleFailsData([]);
        const body = {
            siteUUID: siteUUID,
            groupName: "",
            integration_type: "",
            start_date: selectedTime === "7D" ? last7 : selectedTime === "1M" ? last30 : last90,
            analyticsType: "schedules"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== scheduleRequestId.current) return;
        cache.current.schedules[selectedTime] = res.data[0];
        setScheduleFailsData(res.data[0].failures || []);
        setScheduleSuccesses(res.data[0].totalSuccess?.total_success || 0)
        setIsLoadingSchedules(false);
    }

    const scheduleFailStats = useMemo(() => {
        if (!scheduleFailsData || scheduleFailsData.length === 0) return { totalFails: 0, successRate: 0, totalSuccess: 0 };

        const totalFails = scheduleFailsData.reduce((total, item) => total + item.disarmed_fail + item.armed_fail, 0);
        const total = totalFails + scheduleSuccesses;
        const successRate = total === 0 ? 0 : (scheduleSuccesses / total * 100).toFixed(1);
        return { totalFails, successRate, totalSuccess: scheduleSuccesses };
    }, [scheduleFailsData, scheduleSuccesses]);

    const scheduleFailStatsRef = useRef(scheduleFailStats);
    scheduleFailStatsRef.current = scheduleFailStats;

    {/*     temporary 
    const getSecurityData = async (selectedTime) => {
        // check cache
        if (cache.current.gateway[selectedTime]) {
            setSecurityData(cache.current.gateway[selectedTime]);
            setIsLoadingSecurity(false);
            return;
        }

        const currentRequestId = ++securityRequestId.current;
        setSecurityData([]);
        setIsLoadingSecurity(true);
        const body = {
            siteUUID: siteUUID,
            groupName: "",
            start_date: selectedTime === "7D" ? last7 : selectedTime === "1M" ? last30 : last90,
            analyticsType: "gateways"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== securityRequestId.current) return;
        const processedData = processGatewayData(res.data[0]);
        cache.current.gateway[selectedTime] = processedData;
        setSecurityData(processedData)
        setIsLoadingSecurity(false);
    }
        */}
        

    const getSecurityStats = (securityData) => {
        if (!securityData || securityData.length === 0) return { totalInvalidPins: 0, hasBatteryData: false, hasSignalData: false, latestBattery: null, latestSignal: null };

        const totalInvalidPins = securityData.reduce((total, item) => total + (item.invalid_pin_count || 0), 0);

        const hasBatteryData = securityData.some(item => item.battery !== null && item.battery !== undefined);
        const hasSignalData = securityData.some(item => item.signal !== null && item.signal !== undefined);

        const latestBattery = securityData.findLast(item => item.battery !== null && item.battery !== undefined)?.battery;
        const latestSignal = securityData.findLast(item => item.signal !== null && item.signal !== undefined)?.signal;

        return { totalInvalidPins, hasBatteryData, hasSignalData, latestBattery, latestSignal };
    }
        

    const cleanPinUsername = (user) => {
        if (!user) return user;

        if (user.includes("Padlock unlocked by")) {
            return user
                .replace("Padlock unlocked by", "")
                .replace("using a Permanent PIN", "")
                .trim();
        }

        return user;
    };

    const getUnlockData = async (selectedTime) => {
        /* const cacheKey = `${selectedLock}-${rangeType}`;

        // check cache
        if (cache.current.locks[cacheKey]) {
            const cached = cache.current.locks[cacheKey];
            setUnlockData(cached.unlocks);
            setTopUnlockData(cached.mostActiveUsers);
            setIsLoadingLocks(false);
            return;
        } */

        const currentRequestId = ++lockRequestId.current;
        setUnlockData([]);
        setIsLoadingLocks(true);
        const body = {
            siteUUID: siteUUID,
            lockID: selectedLock,
            start_date: selectedTime === "7D" ? last7 : selectedTime === "1M" ? last30 : last90,
            analyticsType: "locks"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== lockRequestId.current) return;
        //cache.current.locks[cacheKey] = res.data[0];

        const cleanedUsers = (res.data[0].mostActiveUsers || []).map(user => ({
            ...user,
            user: cleanPinUsername(user.user)
        }));

        setUnlockData(res.data[0].unlocks)
        setTopUnlockData(cleanedUsers)
        setIsLoadingLocks(false);
    }

    const getLockStats = (unlockData) => {
        if (!unlockData || unlockData.length === 0) return { totalUnlocks: 0 }
        const totalUnlocks = unlockData.reduce((total, item) => total + item.unlocked_by_count + item.lock_unlock_open_count, 0);
        return { totalUnlocks }
    }

    const getOutputData = async (rangeType, outputPin, selectedTime) => {
        const cacheKey = `${outputPin}-${rangeType}`;

        // check cache
        if (cache.current.outputs[cacheKey]) {
            const cached = cache.current.outputs[cacheKey];
            setOutputData(cached.actions || []);
            setTopOutputData(cached.mostActiveUsers || []);
            setIsLoadingLocks(false);
            return;
        }

        const currentRequestId = ++lockRequestId.current;
        setOutputData([]);
        setIsLoadingLocks(true);

        const body = {
            siteUUID: siteUUID,
            pin: outputPin,
            start_date: selectedTime === "7D" ? last7 : selectedTime === "1M" ? last30 : last90,
            analyticsType: "outputs"
        }
        const res = await getSiteAnalytics(apiData, body, dispatch)
        if (currentRequestId !== lockRequestId.current) return;
        cache.current.outputs[cacheKey] = res.data?.[0];
        setOutputData(res.data?.[0]?.actions || [])
        setTopOutputData(res.data?.[0]?.mostActiveUsers || [])
        setIsLoadingLocks(false);
    }
    
    // for 3 months - outputs
    const processedOutputData = useMemo(() => {
    if (selectedTime !== "3M") return outputData;

    const map = {};

    outputData.forEach(item => {
        const date = new Date(item.event_date);
        const key = date.getMonth();

        if (!map[key]) {
            map[key] = { ...item };
        } else {
            map[key].arm_count += item.arm_count;
            map[key].disarm_count += item.disarm_count;
            map[key].pulse_count += item.pulse_count;
        }
    });

    return Object.values(map);
    }, [outputData, selectedTime]);

    // for 3 months - locks
    const processedUnlockData = useMemo(() => {
    if (selectedTime !== "3M") return unlockData;

    const map = {};

    unlockData.forEach(item => {
        const date = new Date(item.event_date);
        const key = date.getMonth();

        if (!map[key]) {
            map[key] = { ...item };
        } else {
            map[key].unlocked_by_count += item.unlocked_by_count;
            map[key].lock_unlock_open_count += item.lock_unlock_open_count;
        }
    });

    return Object.values(map);
    }, [unlockData, selectedTime]);

     // for 3 months - schedules
    const processedScheduleData = useMemo(() => {
    if (selectedTime !== "3M") return scheduleFailsData ?? [];

    const map = {};

    scheduleFailsData?.forEach(item => {
        const date = new Date(item.event_date);
        const key = date.getMonth();

        if (!map[key]) {
            map[key] = { ...item };
        } else {
            map[key].armed_fail += item.armed_fail;
            map[key].disarmed_fail += item.disarmed_fail;
        }
    });

    return Object.values(map);
    }, [scheduleFailsData, selectedTime]);

    // for 3 months - gateway
    const processedSecurityData = useMemo(() => {
    if (selectedTime !== "3M") return securityData ?? [];

    const map = {};

    securityData.forEach(item => {
        const date = new Date(item.event_date);
        const key = date.getMonth();

        if (!map[key]) {
            map[key] = { ...item };
        } else {
            map[key].invalid_pin_count += item.invalid_pin_count;
            if (item.battery !== null) map[key].battery = item.battery;
            if (item.signal !== null) map[key].signal = item.signal;
        }
    });

    return Object.values(map).sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    }, [securityData, selectedTime]);

    const formatEventDate = (dateStr, period) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        

        switch (period) {
        case '7D':
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        case '1M':
            return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        case '3M':
            return date.toLocaleDateString('en-US', { month: 'short' });
        default:
            return dateStr;
        }
    };


// charts -------------------------------------------------

    const barChartData = {
        labels: processedScheduleData.map(item => formatEventDate(item.event_date, selectedTime)),
        datasets: [
            {
                label: 'Failed Arm Schedules',
                data: processedScheduleData.map(item => item.armed_fail),
                backgroundColor: C.red
            },
            {
                label: 'Failed Disarm Schedules',
                data: processedScheduleData.map(item => item.disarmed_fail),
                backgroundColor: C.orange
            }
        ]
    }

    const doughnutData = {
        labels: ['Success', 'Arm Failed', 'Disarm Failed'],
        datasets: [
            {
                data: [scheduleSuccesses || 0, scheduleFailsData?.reduce((total, item) => total + item.armed_fail, 0), scheduleFailsData?.reduce((total, item) => total + item.disarmed_fail, 0)],
                backgroundColor: [C.green, C.red, C.orange]
            }
        ]
    };

    const lineData1 = {
        labels: processedSecurityData.map(item => formatEventDate(item.event_date, selectedTime)),
        datasets: [
            {
                type: 'line',
                label: "Invalid PIN Attempts",
                data: processedSecurityData.map(item => item.invalid_pin_count),
                fill: true,
                backgroundColor: 'rgba(255, 99, 132, 0.01)',
                borderColor: C.red,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                yAxisID: 'y1'
            },
           {
                type: 'bar',
                label: selectedTime === "3M" ? "Last Battery Level" : "Battery Level",
                data: processedSecurityData.map(item => item.battery),
                backgroundColor: C.green,
                borderRadius: 4,
                yAxisID: 'y'
           },
           {
                type: 'bar',
                label: selectedTime === "3M" ? "Last Signal Strength" : "Signal Strength",
                data: processedSecurityData.map(item => item.signal),
                backgroundColor: C.lightBlue,
                borderRadius: 4,
                yAxisID: 'y'
           }
        ]
    };

    const lineData1Options = {
        scales: {
            y: {
                type: 'linear',
                position: 'left',
                title: {
                    display: true,
                    text: 'Percentage (%)'
                },
                min: 0,
                max: 100
            },
            y1: {
                type: 'linear',
                position: 'right',
                title: {
                    display: true,
                    text: 'Invalid PINs'
                },
                ticks: {
                    stepSize: 1,
                    precision: 0
                },
                grid: {
                    drawOnChartArea: false
                }
            }
        }
    };

    const lineData2 = {
        labels: processedUnlockData.map(item => formatEventDate(item.event_date, selectedTime)),
        datasets: [
            {
                label: `Opens via App`,
                data: processedUnlockData.map(item => item.unlocked_by_count),
                fill: true,
                backgroundColor: 'rgba(128, 119, 221, 0.01)',
                borderColor: 'rgba(128, 119, 221, 1)',
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8
            },
            {
                label: "Opens via Pin",
                data: processedUnlockData.map(item => item.lock_unlock_open_count),
                fill: true,
                backgroundColor: 'rgba(221, 81, 68, 0.01)',
                borderColor: 'rgba(221, 80, 68, 1)',
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8
            }
        ]
    };

    const fallbackLineData2 = {
        labels: processedUnlockData.map(item => formatEventDate(item.event_date, selectedTime)),
        datasets: [
            {
                label: `Opens via App`,
                data: processedUnlockData.map(item => item.unlocked_by_count),
                backgroundColor: 'rgb(0, 200, 255)',
                borderWidth: 1,
                borderRadius: 8,
            },
            {
                label: "Opens via Pin",
                data: processedUnlockData.map(item => item.lock_unlock_open_count),
                backgroundColor: C.red,
                borderWidth: 1,
                borderRadius: 8,
            }
        ]
    };

    const barData2 = {
        labels: topUnlockData.slice(0, 5).map(item => {
            if (item.id == null) {
                const match = item.user.match(/unlocked by (.+?) using a/);
                const name = match ? match[1] : item.user;
                return `${name} - Pin`;
            }
            return `${item.user} - App`;
        }),
        datasets: [
            {
                label: "Unlocks",
                data: topUnlockData.slice(0, 5).map(item => item.count),
                backgroundColor: ["#60e08f", "#6298ee", "#efbc65", "#df7474", "#75c71e"],
                borderWidth: 1,
                borderRadius: 8,
            }
        ]
    };

    const outputLineData = {
        labels: processedOutputData.map(item => formatEventDate(item.event_date, selectedTime)),
        datasets: [
            {
                label: "Arms",
                data: processedOutputData.map(item => item.arm_count),
                backgroundColor: 'rgba(98, 152, 238, 0.01)',
                borderColor: C.blue,
                borderWidth: 2,
                borderRadius: 8,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
            },
            {
                label: "Disarms",
                data: processedOutputData.map(item => item.disarm_count),
                backgroundColor: 'rgba(96, 224, 143, 0.01)',
                borderColor: C.green,
                borderWidth: 2,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
                
            },
            {
                label: "Pulses",
                data: processedOutputData.map(item => item.pulse_count),
                backgroundColor: 'rgba(238, 109, 109, 0.01)',
                borderColor: C.red,
                borderWidth: 2,
                borderRadius: 8,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
            }
        ]
    };

    const fallbackOutputBarData = {
        labels: processedOutputData.map(item => formatEventDate(item.event_date, selectedTime)),
        datasets: [
            {
                label: "Arms",
                data: outputData.map(item => item.arm_count),
                backgroundColor: C.blue,
                borderWidth: 1,
                borderRadius: 8,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
            },
            {
                label: "Disarms",
                data: outputData.map(item => item.disarm_count),
                backgroundColor: C.green,
                borderWidth: 1,
                borderRadius: 8,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
            },
            {
                label: "Pulses",
                data: outputData.map(item => item.pulse_count),
                backgroundColor: C.red,
                borderWidth: 1,
                borderRadius: 8,
                tension: 0.5,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
            }
        ]
    };
    const outputBarData = {
        labels: topOutputData.slice(0, 5).map(item => item.user),
        datasets: [
            {
                label: "Actions",
                data: topOutputData.slice(0, 5).map(item => item.count),
                backgroundColor: ["#60e08f", "#6298ee", "#efbc65", "#df7474", "#75c71e"],
                borderWidth: 1,
                borderRadius: 8,
            }
        ]
    };


    // schedule check
    useEffect(() => {
        const checkSchedules = async () => {
            const res = await getSchedules(apiData, siteUUID, dispatch);
            setHasSchedules(res.data && res.data.length > 0);
            setIsCheckingTabs(false);
        };
        if (siteUUID && apiData) checkSchedules();

        mixpanel.track("Analytics tab visited", {
              user: localStorage.getItem('email')
        });
    }, [siteUUID])

    useEffect(() => {
        if (siteUUID && apiData && !isCheckingTabs && tabs[activeTab] === "Schedules") {
            getScheduleData(selectedTime)
        }
    }, [selectedTime, siteUUID, activeTab, isCheckingTabs])

    {/*     temporary
    useEffect(() => {
        if (siteUUID && apiData && !isCheckingTabs && tabs[activeTab] === "Gateway") {
            getSecurityData(selectedTime)
        }
    }, [selectedTime, siteUUID, activeTab, isCheckingTabs])
    */}

    useEffect(() => {
        const selectedOption = dropdownOptions.find(option => option.value === selectedLock);
        if (siteUUID && apiData && !isCheckingTabs && selectedLock && tabs[activeTab] === "User Analytics" && selectedOption?.type === 'padlock') {
            getUnlockData(selectedTime)
        }

        if (siteUUID && apiData && !isCheckingTabs && selectedLock && tabs[activeTab] === "User Analytics" && selectedOption?.type === 'output') {
            getOutputData(selectedTime, selectedOption.pin, selectedTime)
        }
    }, [selectedTime, selectedLock, activeTab, isCheckingTabs])

    // first dropdown option on open
    useEffect(() => {
        if (tabs[activeTab] === "User Analytics" && !selectedLock && dropdownOptions.length > 0) {
            setSelectedLock(dropdownOptions[0].value);
        }
    }, [activeTab, dropdownOptions, selectedLock])

    if (isCheckingTabs) {
        return (
            <div style={{ padding: "20px" }}>
                <div style={{ display: "flex", gap: "48px", marginLeft: "8px", marginBottom: "20px" }}>
                    <div className={`${classes.skeleton}`} style={{ width: "100px", height: "30px" }} />
                    <div className={`${classes.skeleton}`} style={{ width: "80px", height: "30px" }} />
                    <div className={`${classes.skeleton}`} style={{ width: "70px", height: "30px" }} />
                </div>
                <div className={`${classes.skeleton} ${classes.skeletonChart}`} />
            </div>
        );
    }

    return (
        <>
        
        <div className={classes.tabs}>
            {tabs.map((tab, i) => (
                <button key={tab} 
                    className={`${classes.tab} ${activeTab === i ? classes.active : ""}`}
                    onClick={() => setActiveTab(i)}>
                    {tab}
                </button>
            ))}
        </div>

        <hr className={classes.divider} />
        
        {tabs[activeTab] === "Schedules" && (
        <div className={isCollapsed ? classes.schedulesContainer : classes.schedulesContainerCol}>
            <div className={classes.doughnutSection}>
                <div className={classes.doughnutHeader}>
                    <div className={classes.title}>
                        <h2>Schedule success rate</h2>
                        <p>Pass/fail breakdown for all schedules</p>
                    </div>
                    <div className={classes.timeButtons}>
                        {times.map((time) => (
                            <button
                                key={time}
                                className={selectedTime === time ? classes.timeButtonActive : classes.timeButton}
                                onClick={() => setSelectedTime(time)}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                </div>
                {isLoadingSchedules ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px" }}>
                        <div className={`${classes.skeleton} ${classes.skeletonDoughnut}`} />
                        <div className={`${classes.skeleton}`} style={{ width: "120px", height: "16px", marginTop: "38px" }} />
                    </div>
                ) :
                scheduleFailStats.successRate === 0 ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "437px" }}>
                        <p style={{ fontSize: "14px", fontWeight: "400", color: "#888888" }}>
                            No data available.
                        </p>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "32px" }}>
                        <div style={{ height: "350px" }}>
                            <Doughnut
                                key={`doughnut-${selectedTime}-${scheduleFailStats.successRate}`}
                                data={doughnutData}
                                options={{
                                    cutout: '60%',
                                    plugins: {
                                        legend: {
                                            position: 'bottom'
                                        }
                                    }
                                }}
                                plugins={[{
                                    id: 'centerText',
                                    beforeDraw: (chart) => {
                                        const { ctx, chartArea } = chart;
                                        if (!chartArea) return;

                                        const centerX = (chartArea.left + chartArea.right) / 2;
                                        const centerY = (chartArea.top + chartArea.bottom) / 2;
                                        const successRate = scheduleFailStatsRef.current.successRate;

                                        ctx.save();
                                        ctx.globalAlpha = 0.6;

                                        ctx.font = "600 28px sans-serif";
                                        ctx.textBaseline = "middle";
                                        ctx.textAlign = "center";
                                        ctx.fillStyle = "#000";
                                        ctx.fillText(`${successRate}%`, centerX, centerY - 8);

                                        ctx.font = "400 12px sans-serif";
                                        ctx.fillText("Success rate", centerX, centerY + 14);

                                        ctx.restore();
                                    }
                                }]}
                            />
                        </div>
                    </div>
                )}
            </div>


            <div style ={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, maxWidth: isCollapsed ? "600px" : "min(85%, 650px)"}}>
                <div className={`${classes.title} ${isCollapsed ? classes.title : classes.titleCol}`} style={{ marginLeft: "22px" }}>
                    <h2>Schedule fails</h2>
                    <p>Failed schedules over time</p>
                </div>
                {isLoadingSchedules ? (
                    <div style={{ padding: "20px" }}>
                        <div className={classes.skeletonStat} style={{ marginLeft: "8px", marginTop: "20px" }}>
                            <div className={`${classes.skeleton} ${classes.skeletonStatLabel}`} />
                            <div className={`${classes.skeleton} ${classes.skeletonStatValue}`} />
                        </div>
                        <div className={`${classes.skeleton} ${classes.skeletonChart}`} style={{ height: "350px" }} />
                    </div>
                ) :
                scheduleFailStats.totalFails === 0 ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "437px" }}>
                        <p style={{ fontSize: "14px", fontWeight: "400", color: "#888888" }}>
                            No schedule fails for the selected time period.
                        </p>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "32px", marginLeft: "8px", width: "100%" }}>
                        <div style={{ height: "350px", width: "min(100%, 600px)" }}>
                            <Bar
                                data={barChartData}
                                options={{
                                        scales: {
                                            y: {
                                                ticks: {
                                                    stepSize: 1,
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }}
                            />
                        </div>

                        <p style={{ fontSize: "14px", fontWeight: "400", opacity: "0.7", marginTop: "24px" }}>
                            Total fails: {scheduleFailStats.totalFails}
                        </p>
                    </div>
                )}
            </div>
        </div>
        )}

        {tabs[activeTab] === "Gateway" && (
            <div>
                <div className={`${classes.securityStatsHeader} ${isCollapsed ? classes.securityStatsHeader : classes.securityStatsHeaderCol}`}>
                    <div className={classes.title}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: isCollapsed ? "space-between" : "", gap: isCollapsed ? "0" : "8px" }}>
                            <h2>Gateway Stats</h2>
                            {gatewayBattery !== null && gatewayBattery < 25 && gatewayBattery > 0 && (
                                <>
                                    <Tooltip key={`critical-${isCollapsed}`} target="#gatewayInfoCritical" position={isCollapsed ? "right" : "top"} className={classes.customTooltip} />
                                    <span id="gatewayInfoCritical" className={classes.redInfoButton} data-pr-tooltip={isOffline ? "Gateway is offline & last battery is critical. Charge immediately." : "Battery critical. Charge immediately."}><img className={classes.infoIcon} src={danger} /></span>
                                </>
                            )}
                            {gatewayBattery !== null && gatewayBattery >= 25 && gatewayBattery < 50 && (
                                <>
                                    <Tooltip key={`low-${isCollapsed}`} target="#gatewayInfoLow" position={isCollapsed ? "right" : "top"} className={classes.customTooltip} />
                                    <span id="gatewayInfoLow" className={classes.orangeInfoButton} data-pr-tooltip={isOffline ? "Gateway is offline & last battery is low. Consider charging." : "Battery is low. Consider charging."}><img className={classes.infoIcon} src={dangerOrange} /></span>
                                </>
                            )}
                            {gatewayBattery >= 50 && isOffline && (
                                <>
                                    <Tooltip key={`offline-${isCollapsed}`} target="#gatewayInfoOffline" position={isCollapsed ? "right" : "top"} className={classes.customTooltip} />
                                    <span id="gatewayInfoOffline" className={classes.blueInfoButton} data-pr-tooltip="Gateway is offline. Battery level may be innacurate. Check connection."><img className={classes.infoIcon} src={dangerBlue} /></span>
                                </>
                            )}
                            {gatewayBattery == 0 && (
                                <>
                                    <Tooltip key={`dead-${isCollapsed}`} target="#gatewayInfoDead" position={isCollapsed ? "right" : "top"} className={classes.customTooltip} />
                                    <span id="gatewayInfoDead" className={classes.redInfoButton} data-pr-tooltip="Gateway battery is dead. Please charge."><img className={classes.infoIcon} src={danger} /></span>
                                </>
                            )}

                        </div>
                        <p>Invalid PINs, battery levels & signal strength</p>
                    </div>
                    <div className={classes.timeButtons}>
                        {times.map((time, i) => (
                            <button 
                                key={time} 
                                className={`classes.timeButton ${selectedTime === time ? classes.timeButtonActive : classes.timeButton}`}
                                onClick={() => setSelectedTime(time)}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                </div>

                {isLoadingSecurity ? (
                    <div style={{ padding: "20px" }}>
                        <div style={{ display: "flex", gap: "39px" }}>
                            <div className={classes.skeletonStat}>
                                <div className={`${classes.skeleton} ${classes.skeletonStatLabel}`} />
                                <div className={`${classes.skeleton} ${classes.skeletonStatValue}`} />
                            </div>
                            <div className={classes.skeletonStat}>
                                <div className={`${classes.skeleton} ${classes.skeletonStatLabel}`} style={{ width: "120px" }} />
                                <div className={`${classes.skeleton} ${classes.skeletonStatValue}`} />
                            </div>
                            <div className={classes.skeletonStat}>
                                <div className={`${classes.skeleton} ${classes.skeletonStatLabel}`} style={{ width: "120px" }} />
                                <div className={`${classes.skeleton} ${classes.skeletonStatValue}`} />
                            </div>
                        </div>
                        <div className={`${classes.skeleton} ${classes.skeletonChart}`} />
                    </div>
                ) : (
                    <>
                        <div className={classes.statsContainer}>
                            <div className={classes.stat}>
                                <p>Invalid PINs</p>
                                <h1>{getSecurityStats(securityData).totalInvalidPins}</h1>
                            </div>
                            <div className={classes.stat}>     
                                <p>Latest Battery Level</p>
                                <h1>{gatewayBattery}%</h1>
                            </div>

                            <div className={classes.stat}>
                                <p>Latest Signal Strength</p>
                                <h1>{getSecurityStats(securityData).latestSignal || 0}%</h1>
                            </div>
                        </div>
                        {!getSecurityStats(securityData).hasBatteryData && !getSecurityStats(securityData).hasSignalData && !getSecurityStats(securityData).totalInvalidPins ? (
                            <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center", color: "#888888" }}>
                                No gateway analytics for the selected time period.
                            </p>
                        ) : (
                            
                        <div className={`${classes.securityChart} ${isCollapsed ? '' : classes.securityChartCol}`}>
                            <Bar
                                data={lineData1}
                                options={lineData1Options}
                            />
                        </div>
                        )}
                        
                    </>
                )}
                

            </div>
            
        )}

        {tabs[activeTab] === "User Analytics" && (
            <>
            <Dropdown
                value={selectedLock}
                options={dropdownOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setSelectedLock(e.value)}
                placeholder="Choose an output or lock"
                className={classes.dropdown}
            />
            {!selectedLock && (
                <div style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center" }}>
                    <p>Please select an output or lock</p>
                </div>
            )}

            {selectedLock && selectedOption?.type === "padlock" && (
            <>
                <div className={isCollapsed ? classes.padlocksContainer : classes.padlocksContainerCol}>
                    <div className={classes.padlocksLeft}>
                            <div className={`${classes.statsHeader} ${isCollapsed ? classes.statsHeader : classes.statsHeaderCol}`}>
                                <div
                                    className={classes.title}
                                >
                                    <h2>Padlock unlocks</h2>
                                    <p>Unlock activity by lock</p>
                                </div>
                                <div className={classes.timeButtons}>
                                    {times.map((time, i) => (
                                        <button
                                        key={time}
                                        className={`classes.timeButton ${selectedTime === time ? classes.timeButtonActive : classes.timeButton}`}
                                        onClick={() => setSelectedTime(time)}
                                    >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {isLoadingLocks ? (
                                <div style={{ padding: "20px" }}>
                                    <div className={classes.skeletonStat} style={{ marginLeft: "4px" }}>
                                        <div className={`${classes.skeleton} ${classes.skeletonStatLabel}`} style={{ width: "40px" }} />
                                        <div className={`${classes.skeleton} ${classes.skeletonStatValue}`} />
                                    </div>
                                    <div className={`${classes.skeleton} ${classes.skeletonChart}`} style={{ height: "280px" }} />
                                </div>
                            ) : getLockStats(unlockData).totalUnlocks === 0 ? (
                                <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center", color : "#888888" }}>
                                    No padlock analytics for the selected time period.
                                </p>
                            ) : (
                            <>
                            <div className={classes.padlockStatsContainer}>
                                    <div className={classes.padlockStat}>
                                        <p>Total</p>
                                        <h1>{getLockStats(unlockData).totalUnlocks}</h1>
                                    </div>
                            </div>
                            {unlockData.length <= 1 ? (
                                <div className={`${classes.padlockChart} ${isCollapsed ? classes.padlockChartCollapsed : classes.padlockChartCol}`}>
                                    <Bar
                                        data={fallbackLineData2}
                                        options={{
                                            scales: {
                                                y: {
                                                    ticks: {
                                                        stepSize: 1,
                                                        precision: 0
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className={`${classes.padlockChart} ${isCollapsed ? classes.padlockChartCollapsed : classes.padlockChartCol}`}>
                                    <Line
                                        data={lineData2}
                                        options={{
                                            scales: {
                                                y: {
                                                    ticks: {
                                                        stepSize: 1,
                                                        precision: 0
                                                    }
                                                }
                                            }
                                        }} />
                                </div>
                            )}
                        </>
                        )}
                        </div>

                    <div style ={{ display: "flex", flexDirection: "column"}}>
                        <div className={`${classes.title} ${isCollapsed ? classes.title : classes.titleCol}`}>
                            <h2>Top 5 padlock users</h2>
                            {selectedTime === "7D" ? (
                                <p>Most active users in the last 7 days</p>
                            ) : selectedTime === "1M" ? (
                                <p>Most active users in the last month</p>
                            ) : (
                                <p>Most active users in the last 3 months</p>
                            )}
                        </div>

                        {isLoadingLocks ? (
                            <div className={`${classes.topUsersChart} ${isCollapsed ? classes.topUsersChartCollapsed : classes.topUsersChartCol}`}>
                                <div className={classes.skeletonBarsContainer}>
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "90%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "75%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "60%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "45%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "30%" }} />
                                </div>
                            </div>
                        ) : (!topUnlockData || topUnlockData.length === 0) ? (
                                <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center", color: "#888888" }}>
                                    No data available.
                                </p>
                        ) : (
                            <div className={`${classes.topUsersChart} ${isCollapsed ? classes.topUsersChartCollapsed : classes.topUsersChartCol}`}>
                                <div className={classes.userLegend}>
                                    {topUnlockData.slice(0, 5).map((item, index) => {
                                        const colors = ["#60e08f", "#6298ee", "#efbc65", "#df7474", "#75c71e"];
                                        return (
                                            <div
                                                key={item.user}
                                                className={classes.userLegendItem}
                                                onClick={() => {
                                                    if (item.id != null && item.id != 18965 && item.id != 4133 && item.id != 17103 && item.id != 17428) {
                                                        console.log('clicked on:', item.user, item.id);
                                                        handleUserClick(item.id, item.user);
                                                    }
                                                }}
                                            >
                                                <span
                                                    className={classes.userLegendDot}
                                                    style={{ backgroundColor: colors[index % colors.length] }}
                                                />
                                                <span>{item.id == null ? (() => { const match = item.user.match(/unlocked by (.+?) using a/); return match ? `${match[1]} - Pin` : `${item.user} - Pin`; })() : `${item.user} - App`}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Bar
                                    data={barData2}
                                    options={{
                                        indexAxis: 'y',
                                        plugins: {
                                            legend: {
                                                display: false
                                            }
                                        }
                                    }}
                                />
                            </div>
                        )}

                    </div>
                </div>

                {topUnlockData.length > 5 && (
                    <div className={`${classes.dataTableWrapper} ${!isCollapsed ? classes.dataTableWrapperCol : ''}`}>
                        <DataTable value={topUnlockData} scrollable scrollHeight="400px" stripedRows style={{ border: "1px solid rgba(0, 0, 0, 0.2)", borderRadius: "8px", padding: "8px"}}>
                            <Column
                                field="user"
                                header="User"
                                body={(rowData) => (
                                    <span
                                        className={`${rowData.id != null && rowData.id !== 18965 && rowData.id !== 4133 && rowData.id !== 17103 && rowData.id !== 17428 ? classes.tableUser : ''}`}
                                        onClick={() => {
                                            if (rowData.id != null && rowData.id !== 18965 && rowData.id !== 4133 && rowData.id !== 17103 && rowData.id !== 17428) {
                                                handleUserClick(rowData.id, rowData.user);
                                            }
                                        }}
                                    >
                                        {rowData.id == null ? (() => { const match = rowData.user.match(/unlocked by (.+?) using a/); return match ? `${match[1]} - Pin` : `${rowData.user} - Pin`; })() : `${rowData.user} - App`}
                                    </span>
                                )}
                            />
                            <Column field="count" header="Unlock Count"></Column>
                        </DataTable>
                    </div>
                )}
            </>
            )}

            {selectedLock && selectedOption?.type === "output" && (
            <>
                <div className={isCollapsed ? classes.padlocksContainer : classes.padlocksContainerCol}>
                    <div className={classes.padlocksLeft}>
                            <div className={`${classes.statsHeader} ${isCollapsed ? classes.statsHeader : classes.statsHeaderCol}`}>
                                <div
                                    className={classes.title}
                                >
                                    <h2>Output activity</h2>
                                    <p>Activity by output</p>
                                </div>
                                <div className={classes.timeButtons}>
                                    {times.map((time, i) => (
                                        <button
                                        key={time}
                                        className={`classes.timeButton ${selectedTime === time ? classes.timeButtonActive : classes.timeButton}`}
                                        onClick={() => setSelectedTime(time)}
                                    >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {isLoadingLocks ? (
                                <div style={{ padding: "20px" }}>
                                    <div className={classes.skeletonStat} style={{ marginLeft: "4px" }}>
                                        <div className={`${classes.skeleton} ${classes.skeletonStatLabel}`} style={{ width: "40px" }} />
                                        <div className={`${classes.skeleton} ${classes.skeletonStatValue}`} />
                                    </div>
                                    <div className={`${classes.skeleton} ${classes.skeletonChart}`} style={{ height: "280px" }} />
                                </div>
                            ) : (!outputData || outputData.length === 0 || outputData.every(item => item.arm_count === 0 && item.disarm_count === 0 && item.pulse_count === 0)) ? (
                                <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center", color: "#888888" }}>
                                    No output analytics for the selected time period.
                                </p>
                            ) : (
                            <>
                            <div className={classes.padlockStatsContainer}>
                                    <div className={classes.padlockStat}>
                                        <p>Total</p>
                                        <h1>{outputData.reduce((total, item) => total + item.arm_count + item.disarm_count + item.pulse_count, 0)}</h1>
                                    </div>
                            </div>
                            {outputData.length <= 1 ? (
                                <div className={`${classes.padlockChart} ${isCollapsed ? classes.padlockChartCollapsed : classes.padlockChartCol}`}>
                                    <Bar
                                        data={fallbackOutputBarData}
                                        options={{
                                            scales: {
                                                y: {
                                                    ticks: {
                                                        stepSize: 1,
                                                        precision: 0
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className={`${classes.padlockChart} ${isCollapsed ? classes.padlockChartCollapsed : classes.padlockChartCol}`}>
                                    <Line
                                        data={outputLineData}
                                        options={{
                                            scales: {
                                                y: {
                                                    ticks: {
                                                        stepSize: 1,
                                                        precision: 0
                                                    }
                                                }
                                            }
                                        }} />
                                </div>
                            )}
                        </>
                        )}
                        </div>

                    <div style ={{ display: "flex", flexDirection: "column"}}>
                        <div className={`${classes.title} ${isCollapsed ? classes.title : classes.titleCol}`}>
                            <h2>Top 5 output users</h2>
                            {selectedTime === "7D" ? (
                                <p>Most active users in the last 7 days</p>
                            ) : selectedTime === "1M" ? (
                                <p>Most active users in the last month</p>
                            ) : (
                                <p>Most active users in the last 3 months</p>
                            )}
                        </div>

                        {isLoadingLocks ? (
                            <div className={`${classes.topUsersChart} ${isCollapsed ? classes.topUsersChartCollapsed : classes.topUsersChartCol}`}>
                                <div className={classes.skeletonBarsContainer}>
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "90%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "75%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "60%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "45%" }} />
                                    <div className={`${classes.skeleton} ${classes.skeletonBar}`} style={{ width: "30%" }} />
                                </div>
                            </div>
                        ) : (!topOutputData || topOutputData.length === 0 || topOutputData.every(item => item.arm_count === 0 && item.disarm_count === 0 && item.pulse_count === 0)) ? (
                                <p style={{ fontSize: "14px", fontWeight: "400", marginTop: "38px", textAlign: "center", color: "#888888" }}>
                                    No data available.
                                </p>
                        ) : (
                            <div className={`${classes.topUsersChart} ${isCollapsed ? classes.topUsersChartCollapsed : classes.topUsersChartCol}`}>
                                <div className={classes.userLegend}> 
                                    {topOutputData.slice(0, 5).map((item, index) => {
                                        const colors = ["#60e08f", "#6298ee", "#efbc65", "#df7474", "#75c71e"];
                                        return (
                                            <div
                                                key={item.user}
                                                className={classes.userLegendItem}
                                                onClick={() => {
                                                    if (item.id != null && item.id != 18965 && item.id != 4133 && item.id != 17103 && item.id != 17428) {
                                                        console.log('clicked on:', item.user, item.id);
                                                        handleUserClick(item.id, item.user);
                                                    }
                                                }}
                                            >
                                                <span
                                                    className={classes.userLegendDot}
                                                    style={{ backgroundColor: colors[index % colors.length] }}
                                                />
                                                <span>{item.user}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Bar
                                    data={outputBarData}
                                    options={{
                                        indexAxis: 'y',
                                        plugins: {
                                            legend: {
                                                display: false
                                            }
                                        }
                                    }}
                                />
                            </div>
                        )}

                    </div>
                </div>
                {topOutputData.length > 5 && (
                    <div className={`${classes.dataTableWrapper} ${!isCollapsed ? classes.dataTableWrapperCol : ''}`}>
                        <DataTable value={topOutputData} scrollable scrollHeight="400px" stripedRows style={{ border: "1px solid rgba(0, 0, 0, 0.2)", borderRadius: "8px", padding: "8px"}}>
                            <Column
                                field="user"
                                header="User"
                                body={(rowData) => (
                                    <span
                                        className={`${rowData.id != null && rowData.id !== 18965 && rowData.id !== 4133 && rowData.id !== 17103 && rowData.id !== 17428 ? classes.tableUser : ''}`}
                                        onClick={() => {
                                            if (rowData.id != null && rowData.id !== 18965 && rowData.id !== 4133 && rowData.id !== 17103 && rowData.id !== 17428) {
                                                handleUserClick(rowData.id, rowData.user);
                                            }
                                        }}
                                    >
                                        {rowData.user}
                                    </span>
                                )}
                            />
                            <Column field="count" header="Activity Count"></Column>
                        </DataTable>
                    </div>
                )}
                
            </>
            )}
            </>
        )}

        </>
    );
}

export default AnalyticsPage;